import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FaUsers,
  FaUserCheck,
  FaUserFriends,
  FaClipboardList,
  FaChalkboardTeacher,
  FaDollarSign,
  FaExclamationTriangle,
  FaArrowUp,
  FaArrowDown,
  FaChartBar,
  FaClock,
  FaFileContract,
  FaEnvelope,
  FaBell,
  FaRobot,
  FaToggleOn,
  FaToggleOff,
  FaBoxOpen,
  FaChevronDown,
  FaChevronRight
} from 'react-icons/fa';
import { AIConsultantsData } from '../../constants/aiAgents';
import useSWR from 'swr';
import Fetcher from '../../lib/Fetcher';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import { formatCurrency } from '../../utils/currencyFormatter';

function AdminDashboard() {
  const { data: stats, error, mutate: mutateStats } = useSWR('/api/admin/dashboard/stats', Fetcher);
  const { data: settingsData, mutate: mutateSettings } = useSWR('/api/admin/public/settings', Fetcher);
  const [isTogglingAi, setIsTogglingAi] = useState(false);

  // Handle errors in useEffect to avoid React warnings about setState during render
  useEffect(() => {
    if (error) {
      // Only show error if it's not a network error (network errors are handled by HttpInterceptor)
      if (error.response && error.response.status >= 500) {
        toast.error('Failed to load dashboard statistics');
      }
    }
  }, [error]);

  const dashboardStats = stats?.data || {
    trainers: { total: 0, pending: 0, active: 0 },
    users: { total: 0, active: 0 },
    students: { total: 0 },
    programs: { total: 0 },
    classes: { total: 0 },
    transactions: { total: 0, revenue: 0 },
    specializationRequests: { pending: 0 },
    recentTransactions: []
  };

  const revenue = dashboardStats.transactions?.revenue || 0;
  const transactionTotal = dashboardStats.transactions?.total || 0;
  const recentTransactions = dashboardStats.recentTransactions || [];
  const globalSettings = settingsData?.data || { isAiAgentsEnabled: true, isMarketplaceMockDataEnabled: true, disabledAiAgents: [] };
  const [isTogglingMockData, setIsTogglingMockData] = useState(false);
  const [togglingAgentId, setTogglingAgentId] = useState(null);
  const [isIndividualTogglesExpanded, setIsIndividualTogglesExpanded] = useState(false);

  const handleToggleAiFeature = async () => {
    try {
      setIsTogglingAi(true);
      const res = await HttpInterceptor.put('/api/admin/settings/global',
        { isAiAgentsEnabled: !globalSettings.isAiAgentsEnabled }
      );
      if (res.data) {
        toast.success(`AI Agents feature ${!globalSettings.isAiAgentsEnabled ? 'enabled' : 'disabled'}`);
        mutateSettings();
      }
    } catch (err) {
      toast.error('Failed to update settings');
    } finally {
      setIsTogglingAi(false);
    }
  };

  const handleToggleAgent = async (agentId) => {
    try {
      setTogglingAgentId(agentId);
      const isDisabled = globalSettings.disabledAiAgents?.includes(agentId);
      const newDisabledList = isDisabled
        ? globalSettings.disabledAiAgents.filter(id => id !== agentId)
        : [...(globalSettings.disabledAiAgents || []), agentId];

      const res = await HttpInterceptor.put('/api/admin/settings/global',
        { disabledAiAgents: newDisabledList }
      );
      if (res.data) {
        toast.success(`${AIConsultantsData.find(a => a.id === agentId)?.name} ${isDisabled ? 'enabled' : 'disabled'}`);
        mutateSettings();
      }
    } catch (err) {
      toast.error('Failed to update agent status');
    } finally {
      setTogglingAgentId(null);
    }
  };

  const handleToggleMockData = async () => {
    try {
      setIsTogglingMockData(true);
      const res = await HttpInterceptor.put('/api/admin/settings/global',
        { isMarketplaceMockDataEnabled: !globalSettings.isMarketplaceMockDataEnabled }
      );
      if (res.data) {
        toast.success(`Marketplace mock data ${!globalSettings.isMarketplaceMockDataEnabled ? 'enabled' : 'disabled'}`);
        mutateSettings();
      }
    } catch (err) {
      toast.error('Failed to update settings');
    } finally {
      setIsTogglingMockData(false);
    }
  };

  // Key metrics - most important stats
  const keyMetrics = [
    {
      title: 'Total Revenue',
      value: formatCurrency(revenue),
      icon: <FaDollarSign className="text-2xl" />,
      color: 'from-purple-500 to-purple-600',
      link: '/admin/transactions',
      subtitle: `${transactionTotal} transactions`
    },
    {
      title: 'Active Business Partners',
      value: dashboardStats.trainers.active,
      icon: <FaUsers className="text-2xl" />,
      color: 'from-blue-500 to-blue-600',
      link: '/admin/trainers',
      subtitle: `${dashboardStats.trainers.total} total`
    },
    {
      title: 'Total Members',
      value: dashboardStats.users.total,
      icon: <FaUserFriends className="text-2xl" />,
      color: 'from-green-500 to-green-600',
      link: '/admin/users',
      subtitle: `${dashboardStats.users.active} active`
    },
    {
      title: 'Pending Approvals',
      value: dashboardStats.trainers.pending,
      icon: <FaUserCheck className="text-2xl" />,
      color: dashboardStats.trainers.pending > 0 ? 'from-yellow-500 to-orange-500' : 'from-gray-400 to-gray-500',
      link: '/admin/pending-approvals',
      subtitle: 'Requires attention'
    }
  ];

  // Secondary metrics
  const secondaryMetrics = [
    {
      title: 'Total Programs',
      value: dashboardStats.programs.total,
      icon: <FaClipboardList />,
      link: '/admin/programs',
    },
    {
      title: 'Total Classes',
      value: dashboardStats.classes.total,
      icon: <FaChalkboardTeacher />,
      link: '/admin/classes',
    },
    {
      title: 'Total Students',
      value: dashboardStats.students.total,
      icon: <FaUserFriends />,
      link: '/admin/students',
    },
    {
      title: 'Specialization Requests',
      value: dashboardStats.specializationRequests.pending,
      icon: <FaExclamationTriangle />,
      link: '/admin/pending-approvals',
      highlight: dashboardStats.specializationRequests.pending > 0
    }
  ];

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">Admin Dashboard</h1>
          <p className="text-sm md:text-base text-gray-600">Overview of your platform statistics and activities</p>
        </div>

        {/* Key Metrics - Large Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {keyMetrics.map((metric, index) => (
            <Link
              key={index}
              to={metric.link}
              className={`group relative bg-gradient-to-br ${metric.color} rounded-xl shadow-lg p-6 text-white hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="bg-white/20 rounded-lg p-3">
                  <div className="text-white">{metric.icon}</div>
                </div>
              </div>
              <h3 className="text-sm font-medium text-white/90 mb-1">{metric.title}</h3>
              <p className="text-2xl md:text-3xl font-bold text-white mb-1">{metric.value}</p>
              {metric.subtitle && (
                <p className="text-xs text-white/80">{metric.subtitle}</p>
              )}
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <svg className="w-5 h-5 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          ))}
        </div>

        {/* Secondary Metrics - Compact Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {secondaryMetrics.map((metric, index) => (
            <Link
              key={index}
              to={metric.link}
              className={`bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-all duration-200 border-l-4 ${metric.highlight ? 'border-orange-500' : 'border-transparent'
                }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-xs text-gray-600 mb-1">{metric.title}</p>
                  <p className="text-xl font-bold text-gray-900">{metric.value}</p>
                </div>
                <div className={`text-gray-400 ${metric.highlight ? 'text-orange-500' : ''}`}>
                  {metric.icon}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Quick Actions & Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Pending Actions Alert */}
          {(dashboardStats.trainers.pending > 0 || dashboardStats.specializationRequests.pending > 0) && (
            <div className="bg-white rounded-lg shadow-md p-5 border-l-4 border-yellow-500">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <FaExclamationTriangle className="text-yellow-500 mr-2" />
                  Action Required
                </h3>
              </div>
              <div className="space-y-2">
                {dashboardStats.trainers.pending > 0 && (
                  <div className="flex items-center justify-between p-2 bg-yellow-50 rounded">
                    <span className="text-sm text-gray-700">
                      {dashboardStats.trainers.pending} Pending Approval{dashboardStats.trainers.pending !== 1 ? 's' : ''}
                    </span>
                  </div>
                )}
                {dashboardStats.specializationRequests.pending > 0 && (
                  <div className="flex items-center justify-between p-2 bg-orange-50 rounded">
                    <span className="text-sm text-gray-700">
                      {dashboardStats.specializationRequests.pending} Specialization Request{dashboardStats.specializationRequests.pending !== 1 ? 's' : ''}
                    </span>
                  </div>
                )}
              </div>
              <Link
                to="/admin/pending-approvals"
                className="mt-3 block w-full text-center py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition-colors text-sm font-medium"
              >
                Review Now
              </Link>
            </div>
          )}

          {/* Recent Transactions */}
          <div className={`bg-white rounded-lg shadow-md p-5 ${(dashboardStats.trainers.pending > 0 || dashboardStats.specializationRequests.pending > 0) ? '' : 'lg:col-span-2'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Recent Transactions</h3>
              <Link
                to="/admin/transactions"
                className="text-sm text-purple-600 hover:text-purple-700 font-medium"
              >
                View All →
              </Link>
            </div>
            {recentTransactions && recentTransactions.length > 0 ? (
              <div className="space-y-2">
                {recentTransactions.slice(0, 3).map((transaction, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{transaction.studentName}</p>
                      <p className="text-xs text-gray-600 truncate">{transaction.programName}</p>
                    </div>
                    <div className="text-right ml-3">
                      <p className="font-bold text-gray-900">₹{transaction.finalAmount}</p>
                      <p className={`text-xs ${transaction.status === 'completed' ? 'text-green-600' :
                        transaction.status === 'pending' ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                        {transaction.status}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <FaDollarSign className="text-4xl text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-600">No recent transactions</p>
              </div>
            )}
          </div>
        </div>

        {/* Management Tools */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Management Tools</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link
              to="/admin/legal-documents"
              className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-all duration-200 group border border-gray-200 hover:border-blue-500"
            >
              <div className="flex items-center mb-3">
                <div className="bg-blue-100 rounded-lg p-3 mr-3 group-hover:bg-blue-500 transition-colors">
                  <FaFileContract className="text-blue-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-semibold text-gray-900">Legal Documents</h3>
              </div>
              <p className="text-sm text-gray-600">Manage Terms of Service and Privacy Policy</p>
            </Link>

            <Link
              to="/admin/newsletter"
              className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-all duration-200 group border border-gray-200 hover:border-green-500"
            >
              <div className="flex items-center mb-3">
                <div className="bg-green-100 rounded-lg p-3 mr-3 group-hover:bg-green-500 transition-colors">
                  <FaEnvelope className="text-green-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-semibold text-gray-900">Newsletter</h3>
              </div>
              <p className="text-sm text-gray-600">Compose and send newsletters to subscribers</p>
            </Link>

            <Link
              to="/admin/notifications"
              className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-all duration-200 group border border-gray-200 hover:border-purple-500"
            >
              <div className="flex items-center mb-3">
                <div className="bg-purple-100 rounded-lg p-3 mr-3 group-hover:bg-purple-500 transition-colors">
                  <FaBell className="text-purple-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-semibold text-gray-900">Notifications</h3>
              </div>
            </Link>
          </div>
        </div>

        {/* Global Settings */}
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Global Features & Settings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow-md p-5 border border-gray-200 flex flex-col justify-between">
              <div className="flex items-center mb-3">
                <div className={`${globalSettings.isAiAgentsEnabled ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-500'} rounded-lg p-3 mr-3 transition-colors`}>
                  <FaRobot className="text-xl" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">AI Agents Feature</h3>
                  <p className="text-sm text-gray-600">Enable or disable AI consultant functionality platform-wide.</p>
                </div>
              </div>
              <button
                onClick={handleToggleAiFeature}
                disabled={isTogglingAi}
                className={`mt-4 w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg font-medium transition-all ${globalSettings.isAiAgentsEnabled
                  ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                  : 'bg-green-50 text-green-600 hover:bg-green-100 border border-green-200'
                  }`}
              >
                {globalSettings.isAiAgentsEnabled ? (
                  <><FaToggleOn className="text-lg" /> Disable AI Agents</>
                ) : (
                  <><FaToggleOff className="text-lg" /> Enable AI Agents</>
                )}
              </button>

              {globalSettings.isAiAgentsEnabled && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <button 
                    onClick={() => setIsIndividualTogglesExpanded(!isIndividualTogglesExpanded)}
                    className="w-full flex items-center justify-between group mb-2 py-1 px-1 rounded hover:bg-gray-50 transition-all text-xs font-bold text-gray-400 uppercase tracking-wider"
                  >
                    <span>Individual Toggles</span>
                    {isIndividualTogglesExpanded ? <FaChevronDown className="text-[10px]" /> : <FaChevronRight className="text-[10px]" />}
                  </button>

                  <div className={`space-y-3 overflow-hidden transition-all duration-300 ease-in-out ${isIndividualTogglesExpanded ? 'max-h-[1000px] opacity-100 mt-2' : 'max-h-0 opacity-0'}`}>
                    {AIConsultantsData.map(agent => {
                      const isDisabled = globalSettings.disabledAiAgents?.includes(agent.id);
                      const isToggling = togglingAgentId === agent.id;
                      return (
                        <div key={agent.id} className="flex items-center justify-between group">
                          <div className="flex items-center gap-2">
                            <img src={agent.profileImage} alt="" className="w-8 h-8 rounded-full object-cover grayscale-[0.5] group-hover:grayscale-0 transition-all border border-gray-100 shadow-sm" />
                            <div className="flex flex-col">
                              <span className={`text-sm font-medium ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>{agent.name.split(' (AI')[0]}</span>
                              <span className={`text-[10px] uppercase font-bold tracking-wider ${isDisabled ? 'text-gray-300' : 'text-purple-400'}`}>{agent.service}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleToggleAgent(agent.id)}
                            disabled={isToggling}
                            className={`text-2xl transition-colors ${isDisabled ? 'text-gray-300 hover:text-gray-400' : 'text-purple-600 hover:text-purple-700'}`}
                          >
                            {isDisabled ? <FaToggleOff /> : <FaToggleOn />}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-md p-5 border border-gray-200 flex flex-col justify-between">
              <div className="flex items-center mb-3">
                <div className={`${globalSettings.isMarketplaceMockDataEnabled ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'} rounded-lg p-3 mr-3 transition-colors`}>
                  <FaBoxOpen className="text-xl" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Marketplace Mock Data</h3>
                  <p className="text-sm text-gray-600">Enable or disable display of mock products in the marketplace.</p>
                </div>
              </div>
              <button
                onClick={handleToggleMockData}
                disabled={isTogglingMockData}
                className={`mt-4 w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg font-medium transition-all ${globalSettings.isMarketplaceMockDataEnabled
                  ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                  : 'bg-green-50 text-green-600 hover:bg-green-100 border border-green-200'
                  }`}
              >
                {globalSettings.isMarketplaceMockDataEnabled ? (
                  <><FaToggleOn className="text-lg" /> Disable Mock Data</>
                ) : (
                  <><FaToggleOff className="text-lg" /> Enable Mock Data</>
                )}
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default AdminDashboard;


